import * as THREE from 'three/webgpu';
import {
    type InteractionVisualStateContext,
    type InteractionVisualStateRuntime,
} from '../../manager/interactionState/AbstractInteractionVisualState';
import type { Viewer } from '../../viewer/Viewer';
import { ImportedModelSelectionObjectResolver } from '../selection/ImportedModelSelectionObjectResolver';
import { SW_HOVER_COLOR, SW_SELECTED_COLOR } from '../render/sw-theme';
import { addNonSelectableObject3DToParent } from '../../utils/scene-object/addObject3DToParent';
import { solidworksDefaultPreset } from '../render/solidworks-default-preset';
import { markSharedInteractionMaterial } from '../../material/materialOwnership';

/**
 * imported-model 设计树移入包围盒描述符。
 */
interface ImportedModelBoundingBoxDescriptor {
    /** 当前包围盒目标键。 */
    key: string;
    /** 当前包围盒。 */
    box: THREE.Box3;
}

/**
 * imported-model 设计树视觉应用器依赖。
 */
export interface ImportedModelFeatureTreeVisualApplicatorDeps {
    /** Viewer 实例。 */
    viewer: Viewer;
    /** 交互视觉状态运行时。 */
    runtime: InteractionVisualStateRuntime;
    /**
     * 快照对象原始材质。
     *
     * 说明：线性零部件阵列等命令态仍可能需要 imported mesh 材质替换，因此保留为可选依赖。
     *
     * @param object 目标对象。
     */
    snapshotOriginalMaterial?: (object: THREE.Mesh | THREE.Line | THREE.Sprite) => void;
    /**
     * 选中 mesh 材质缓存键。
     *
     * 说明：不同 Viewer 模式可复用同一材质，也可按需拆分缓存命名空间。
     */
    selectedMeshMaterialCacheKey?: string;
}

/**
 * imported-model 设计树视觉应用器。
 *
 * 设计说明：
 * 1. 直接打开导入零件（`ViewerMode.Default`）与装配体模式都需要同一套设计树 hover / select 视觉语义；
 * 2. 该应用器只处理“特征树程序化交互”分支，不接管视口射线拾取或 Mate 命令态拓扑 overlay；
 * 3. 通过从 `AssemblyTransitionState` 抽取并复用，避免 Default / Assembly 两套逻辑漂移。
 */
export class ImportedModelFeatureTreeVisualApplicator {
    /** 运行时依赖。 */
    private readonly deps: ImportedModelFeatureTreeVisualApplicatorDeps;
    /** imported-model 选择对象归一化解析器。 */
    private readonly importedModelSelectionObjectResolver: ImportedModelSelectionObjectResolver;
    /** 当前移入包围盒 helper 映射。 */
    private readonly hoverBoundingBoxHelpers: Map<string, THREE.Box3Helper> = new Map();

    /**
     * 创建 imported-model 设计树视觉应用器。
     *
     * @param deps 运行时依赖。
     */
    public constructor(deps: ImportedModelFeatureTreeVisualApplicatorDeps) {
        this.deps = deps;
        this.importedModelSelectionObjectResolver = new ImportedModelSelectionObjectResolver({
            scene: deps.viewer.scene,
            getSceneObjectById: (objectId: string) => deps.viewer.findSceneObjectById(objectId),
        });
    }

    /**
     * 应用 imported-model 设计树视觉状态。
     *
     * @param context 当前刷新上下文。
     */
    public applyFeatureTreeVisualState(context: InteractionVisualStateContext): void {
        this.applyHoverBoundingBoxes(context);
    }

    /**
     * 重置 imported-model 设计树附加视觉资源。
     */
    public resetVisualState(): void {
        this.clearHoverBoundingBoxHelper();
    }

    /**
     * 生成剥离 imported-model 树语义后的基础上下文。
     *
     * 设计说明：
     * 1. 特征树 imported-model 交互不应再让基础状态机递归处理语义 Group；
     * 2. 否则树 hover / selected 会把其子树里已经挂载的 `FeatureFace / FeatureLine / FeaturePoint`
     *    一并写成 Hover / Selected，重新回到高频代理高亮路径；
     * 3. 这里先把“树交互里的 imported-model 语义对象”从默认状态机上下文里剥离，
     *    再由本应用器专门输出包围盒或 visible mesh 高亮。
     *
     * @param context 原始刷新上下文。
     * @returns 供默认状态机使用的基础上下文。
     */
    public createFilteredBaseContext(context: InteractionVisualStateContext): InteractionVisualStateContext {
        const filteredHoveredObject = (
            context.hoveredInteractionMeta?.source === 'feature-tree'
            && this.importedModelSelectionObjectResolver.isImportedModelObject(context.hoveredObject)
        )
            ? null
            : context.hoveredObject;
        const filteredSelectedObjects = context.selectedInteractionMeta?.source === 'feature-tree'
            ? context.selectedObjects.filter((selectedObject) => {
                return !this.importedModelSelectionObjectResolver.isImportedModelObject(selectedObject);
            })
            : context.selectedObjects;

        return {
            ...context,
            hoveredObject: filteredHoveredObject,
            selectedObjects: filteredSelectedObjects,
        };
    }

    /**
     * 判断对象是否为 imported-model 子树里的运行时拓扑代理。
     *
     * @param object 待判断对象。
     * @returns 命中 imported-model Face / Line / Point 代理时返回 `true`。
     */
    public isImportedModelTopologyProxyObject(object: THREE.Object3D): boolean {
        const isTopologyProxyObject = Reflect.get(object, 'isFeatureFace') === true
            || Reflect.get(object, 'isFeatureLine') === true
            || Reflect.get(object, 'isFeaturePoint') === true;
        if (!isTopologyProxyObject) {
            return false;
        }

        return this.importedModelSelectionObjectResolver.isImportedModelObject(object);
    }

    /**
     * 收集 imported-model 语义对象下可用于 mesh 高亮的 visible mesh。
     *
     * @param rootObject 语义根对象。
     * @param options 收集选项。
     * @returns visible mesh 列表。
     */
    public collectImportedVisibleMeshes(
        rootObject: THREE.Object3D,
        options: {
            explicitObjectIds?: readonly string[];
        } = {},
    ): THREE.Mesh[] {
        const explicitObjectIds = Array.isArray(options.explicitObjectIds)
            ? options.explicitObjectIds.filter((objectId) => typeof objectId === 'string' && objectId.length > 0)
            : [];
        const candidateRoots: THREE.Object3D[] = [];

        if (explicitObjectIds.length > 0) {
            if (explicitObjectIds.includes(rootObject.uuid)) {
                candidateRoots.push(rootObject);
            }
            explicitObjectIds.forEach((objectId) => {
                if (objectId === rootObject.uuid) {
                    return;
                }
                const matchedObject = this.deps.viewer.findSceneObjectById(objectId);
                if (matchedObject) {
                    candidateRoots.push(matchedObject);
                }
            });
        } else {
            candidateRoots.push(rootObject);
        }

        const visibleMeshMap = new Map<string, THREE.Mesh>();
        candidateRoots.forEach((candidateRoot) => {
            const candidateObjects: THREE.Object3D[] = [
                candidateRoot,
                ...(this.deps.viewer.listImportedModelInstancedSourceMeshes?.(candidateRoot) ?? []),
            ];
            candidateObjects.forEach((candidateObject) => candidateObject.traverse((object) => {
                if (!(object instanceof THREE.Mesh)) {
                    return;
                }
                if (!object.visible) {
                    return;
                }
                if (object.userData?.skipSelection || object.userData?.isImportedModelOverlay) {
                    return;
                }
                if (this.isImportedModelTopologyProxyObject(object)) {
                    return;
                }
                if (object.name.startsWith('imported-topology-host:')) {
                    return;
                }
                this.deps.viewer.prepareImportedModelSourceMeshForSpatialRead?.(object);
                visibleMeshMap.set(object.uuid, object);
            }));
        });

        return Array.from(visibleMeshMap.values());
    }

    /**
     * 获取 imported-model 设计树选中 mesh 高亮材质。
     *
     * 说明：该材质仅供线性零部件阵列等命令态复用，设计树整模型使用独立的 Selected 渲染配方。
     *
     * @returns 共享高亮材质。
     */
    public getImportedModelTreeSelectedMeshMaterial(): THREE.MeshStandardMaterial {
        const cacheKey = this.deps.selectedMeshMaterialCacheKey
            ?? 'imported-model-feature-tree-selected-mesh';
        const cachedMaterial = this.deps.runtime.stateMeshMaterialCache.get(cacheKey);
        if (cachedMaterial instanceof THREE.MeshStandardMaterial) {
            return cachedMaterial;
        }

        const nextMaterial = markSharedInteractionMaterial(new THREE.MeshStandardMaterial({
            color: SW_SELECTED_COLOR,
            transparent: true,
            opacity: 0.36,
            metalness: solidworksDefaultPreset.material.baseMetalness,
            roughness: solidworksDefaultPreset.material.baseRoughness,
            depthTest: true,
            depthWrite: false,
            side: THREE.DoubleSide,
        }));
        this.deps.runtime.stateMeshMaterialCache.set(cacheKey, nextMaterial);
        return nextMaterial;
    }

    /**
     * 获取 imported Body 整模型 Selected 覆盖材质。
     *
     * 设计说明：
     * 1. 该材质只应用到借用正式渲染几何的普通 Mesh 或单槽实例 Selected 投影，不修改正式显示材质；
     * 2. 配方与 CrownCAD 整模型 Selected 保持一致，使用 MeshPhongMaterial 半透明覆盖；
     * 3. 不设置 polygon offset，让加载阶段已向后偏移的正式表面与正常深度覆盖层自然分离；
     * 4. 共享模板由视觉状态运行时释放；短生命周期覆盖 Mesh 克隆独占材质，以释放各自渲染绑定。
     *
     * @returns 整模型覆盖对象使用的共享 Selected 配方，不应直接承接临时对象的渲染绑定。
     */
    public getImportedModelTreeSelectedBodyMaterial(): THREE.MeshPhongMaterial {
        const cacheKey = 'imported-model-feature-tree-selected-body';
        const cachedMaterial = this.deps.runtime.stateMeshMaterialCache.get(cacheKey);
        if (cachedMaterial instanceof THREE.MeshPhongMaterial) {
            return cachedMaterial;
        }

        const material = markSharedInteractionMaterial(new THREE.MeshPhongMaterial({
            color: SW_SELECTED_COLOR,
            emissive: 0x000000,
            specular: 0xf0f0f0,
            shininess: 5,
            reflectivity: 0.63,
            side: THREE.DoubleSide,
            transparent: true,
            opacity: 0.7,
        }));
        material.name = '导入 Body-整模型选中覆盖材质';
        this.deps.runtime.stateMeshMaterialCache.set(cacheKey, material);
        return material;
    }

    /**
     * 应用 imported-model 移入包围盒。
     *
     * @param context 当前刷新上下文。
     */
    private applyHoverBoundingBoxes(context: InteractionVisualStateContext): void {
        const boundingBoxDescriptors = this.resolveHoverBoundingBoxDescriptors(context);
        if (boundingBoxDescriptors.length === 0) {
            this.clearHoverBoundingBoxHelper();
            return;
        }

        const activeKeys = new Set(boundingBoxDescriptors.map((descriptor) => descriptor.key));
        Array.from(this.hoverBoundingBoxHelpers.keys()).forEach((key) => {
            if (!activeKeys.has(key)) {
                this.disposeHoverBoundingBoxHelper(key);
            }
        });

        boundingBoxDescriptors.forEach((descriptor) => {
            const currentHelper = this.hoverBoundingBoxHelpers.get(descriptor.key);
            if (!currentHelper) {
                this.hoverBoundingBoxHelpers.set(
                    descriptor.key,
                    this.createHoverBoundingBoxHelper(descriptor.box),
                );
                return;
            }

            currentHelper.box.copy(descriptor.box);
            currentHelper.visible = true;
            currentHelper.updateMatrixWorld(true);
        });
    }

    /**
     * 创建 imported-model 移入包围盒 helper。
     *
     * @param box 当前世界包围盒。
     * @returns 包围盒 helper。
     */
    private createHoverBoundingBoxHelper(box: THREE.Box3): THREE.Box3Helper {
        const helper = new THREE.Box3Helper(box.clone(), SW_HOVER_COLOR);
        const helperMaterial = helper.material;
        if (Array.isArray(helperMaterial)) {
            helperMaterial.forEach((material) => {
                material.depthTest = false;
                material.depthWrite = false;
                material.transparent = true;
                material.opacity = 1;
            });
        } else {
            helperMaterial.depthTest = false;
            helperMaterial.depthWrite = false;
            helperMaterial.transparent = true;
            helperMaterial.opacity = 1;
        }
        helper.renderOrder = 8;
        helper.userData.skipSelection = true;
        helper.userData.isAssemblyHoverBoundingBox = true;

        addNonSelectableObject3DToParent(helper, this.deps.viewer.sceneHelpers);
        return helper;
    }

    /**
     * 解析当前 imported-model 移入应显示的全部包围盒描述符。
     *
     * @param context 当前刷新上下文。
     * @returns 包围盒描述符列表。
     */
    private resolveHoverBoundingBoxDescriptors(
        context: InteractionVisualStateContext,
    ): ImportedModelBoundingBoxDescriptor[] {
        const descriptors: ImportedModelBoundingBoxDescriptor[] = [];
        const singleDescriptor = this.resolveSingleHoverBoundingBoxDescriptor(context);
        if (singleDescriptor) {
            descriptors.push(singleDescriptor);
        }

        if (context.programmaticHoveredInteractionMeta?.source !== 'feature-tree') {
            return descriptors;
        }

        context.programmaticHoveredObjects.forEach((hoveredObject) => {
            const descriptor = this.resolveBoundingBoxDescriptor(
                hoveredObject,
                context.programmaticHoveredInteractionMeta,
            );
            if (descriptor) {
                descriptors.push(descriptor);
            }
        });

        return descriptors;
    }

    /**
     * 解析当前 imported-model 移入应显示的单个包围盒描述符。
     *
     * @param context 当前刷新上下文。
     * @returns 包围盒描述符；无需显示时返回 `null`。
     */
    private resolveSingleHoverBoundingBoxDescriptor(
        context: InteractionVisualStateContext,
    ): ImportedModelBoundingBoxDescriptor | null {
        if (context.hoveredInteractionMeta?.source !== 'feature-tree') {
            return null;
        }

        const hoveredObject = context.hoveredObject;
        if (!hoveredObject) {
            return null;
        }

        return this.resolveBoundingBoxDescriptor(hoveredObject, context.hoveredInteractionMeta);
    }

    /**
     * 按指定对象和交互语义解析 imported-model 包围盒。
     *
     * @param hoveredObject 当前移入对象。
     * @param interactionMeta 当前移入语义。
     * @returns 包围盒描述符；无需显示时返回 `null`。
     */
    private resolveBoundingBoxDescriptor(
        hoveredObject: THREE.Object3D,
        interactionMeta: InteractionVisualStateContext['hoveredInteractionMeta'],
    ): ImportedModelBoundingBoxDescriptor | null {
        if (!this.importedModelSelectionObjectResolver.isImportedModelObject(hoveredObject)) {
            return null;
        }

        const explicitObjectIds = Array.isArray(interactionMeta?.entityHighlightTargets)
            ? interactionMeta.entityHighlightTargets.map((target) => target.objectId)
            : [];
        const targetObject = interactionMeta?.glbNodeRole === 'body'
            ? this.importedModelSelectionObjectResolver.resolveSelectionObject(hoveredObject) ?? hoveredObject
            : this.importedModelSelectionObjectResolver.resolveTransformRootObject(hoveredObject)
                ?? this.importedModelSelectionObjectResolver.resolveSelectionObject(hoveredObject)
                ?? hoveredObject;
        const targetMeshes = this.collectImportedVisibleMeshes(targetObject, { explicitObjectIds });
        if (targetMeshes.length <= 0) {
            const box = new THREE.Box3().setFromObject(targetObject);
            return box.isEmpty()
                ? null
                : {
                    key: targetObject.uuid,
                    box,
                };
        }

        const box = new THREE.Box3();
        targetMeshes.forEach((targetMesh) => {
            box.expandByObject(targetMesh);
        });
        if (box.isEmpty()) {
            return null;
        }

        return {
            key: explicitObjectIds.length > 0
                ? explicitObjectIds.join('|')
                : targetMeshes.map((targetMesh) => targetMesh.uuid).join('|'),
            box,
        };
    }

    /**
     * 清理当前移入包围盒 helper。
     */
    private clearHoverBoundingBoxHelper(): void {
        Array.from(this.hoverBoundingBoxHelpers.keys()).forEach((key) => {
            this.disposeHoverBoundingBoxHelper(key);
        });
    }

    /**
     * 释放指定包围盒 helper。
     *
     * @param key helper 键。
     */
    private disposeHoverBoundingBoxHelper(key: string): void {
        const helper = this.hoverBoundingBoxHelpers.get(key);
        if (!helper) {
            return;
        }

        if (helper.parent) {
            helper.parent.remove(helper);
        }
        helper.geometry.dispose();
        const helperMaterial = helper.material;
        if (Array.isArray(helperMaterial)) {
            helperMaterial.forEach((material) => material.dispose());
        } else {
            helperMaterial.dispose();
        }
        this.hoverBoundingBoxHelpers.delete(key);
    }
}
